/**
 * The three ways an agent chat pane talks about its own failures.
 *
 * Five panes (page builder, discovery, knowledge, structure planner, site
 * scaffold, news beat) had grown near-identical copies of this markup, and all
 * of them printed the backend's exception text verbatim. That is where
 * `anthropic.RateLimitError: model claude-sonnet-4 …` lands in front of a
 * customer — the single most direct contradiction of "they should not worry
 * about how we come up with the good results". Two of them also told the
 * customer to restart `main-api.py`.
 *
 * The fix is not to hide failure. A user watching a chat that silently stopped
 * is worse off than one reading a stack trace. So each of these BRANCHES: an
 * admin with internals revealed sees exactly what it always said, everyone else
 * sees the same fact in language that is about their work rather than our
 * servers.
 */
import { useShowInternals } from '../../contexts/InternalsContext';
import { AgentModelErrorLink } from './AgentModelChip';

/**
 * What a raw chat `status` means to someone who did not write the state machine.
 * Unknown values fall through to the raw string, which only admins ever see.
 */
const STATUS_LABEL: Record<string, string> = {
  thinking: 'working',
  failed: 'stopped',
  idle: 'ready',
  accepted: 'accepted',
};

export function AgentStatusChip({
  status,
  className = '',
}: {
  status: string | null | undefined;
  className?: string;
}) {
  const showInternals = useShowInternals();
  if (!status) return null;
  const label = showInternals ? status : (STATUS_LABEL[status] ?? 'working');
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${
        status === 'thinking'
          ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
          : status === 'failed'
            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
      } ${className}`}
    >
      {label}
    </span>
  );
}

/** What we say instead of the provider's exception. */
const GENERIC_ERROR = 'Something went wrong on our side. Try again.';

/**
 * The red bar over a chat whose last turn failed.
 *
 * `stage` is only used for the admin's "adjust this agent's model" link, which
 * gates itself — pass it freely.
 */
export function AgentErrorBanner({
  error,
  stage,
  className = '',
}: {
  error: string | null | undefined;
  stage?: string | null;
  className?: string;
}) {
  const showInternals = useShowInternals();
  if (!error) return null;
  return (
    <div
      className={`px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800 ${className}`}
    >
      {showInternals ? error : GENERIC_ERROR}
      {stage && <AgentModelErrorLink stage={stage} />}
    </div>
  );
}

/**
 * The full-pane state when the chat itself could not be loaded.
 *
 * The "restart main-api.py" hint is real advice — to exactly one person. It
 * stays for them and disappears for everyone else, who get the retry button that
 * was always the actual next step.
 */
export function AgentLoadError({
  error,
  onRetry,
}: {
  error: string | null | undefined;
  onRetry: () => void;
}) {
  const showInternals = useShowInternals();
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-2 p-4 text-xs">
      <div className="text-red-700 dark:text-red-300 text-center">
        {showInternals ? error : "This chat could not be loaded."}
      </div>
      {showInternals && (
        <div className="text-gray-500 dark:text-gray-400 text-center">
          If the backend was just updated, restart{' '}
          <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">main-api.py</code>{' '}
          and refresh.
        </div>
      )}
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
      >
        Retry
      </button>
    </div>
  );
}
